'use client';

import { useEffect, useState } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import { CountsForCategoryRankingField } from '@/features/tournament-wizard/blocks';
import {
  CATEGORY_RANKING_TARGET,
  type Category,
  type CategoryRankingTarget,
} from '@/types/tournament.model';

interface EditCountsForCategoryRankingModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentValue: boolean;
  currentTarget: CategoryRankingTarget;
  currentCategory: Category | null;
  categories: Category[];
  onSave: (
    value: boolean,
    target: CategoryRankingTarget,
    category: Category | null,
  ) => Promise<boolean>;
  isLoading: boolean;
}

export function EditCountsForCategoryRankingModal({
  isOpen,
  onClose,
  currentValue,
  currentTarget,
  currentCategory,
  categories,
  onSave,
  isLoading,
}: EditCountsForCategoryRankingModalProps) {
  const [value, setValue] = useState(currentValue);
  const [target, setTarget] = useState(currentTarget);
  const [category, setCategory] = useState(currentCategory);

  useEffect(() => {
    if (!isOpen) return;
    setValue(currentValue);
    setTarget(currentTarget);
    setCategory(currentCategory);
  }, [isOpen, currentValue, currentTarget, currentCategory]);

  // The backend rejects FIXED_CATEGORY without a target category inside the
  // tournament's range, so block the save instead of surfacing a 400.
  const canSave =
    !value ||
    target !== CATEGORY_RANKING_TARGET.FIXED_CATEGORY ||
    categories.some((c) => c.id === category?.id);

  const handleSave = async () => {
    await onSave(value, target, category);
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Ranking de Categoría"
      onSave={handleSave}
      isLoading={isLoading}
      disabled={!canSave}
    >
      <div className="py-4">
        <CountsForCategoryRankingField
          value={value}
          onChange={setValue}
          wrapInCard={false}
          inputId="edit-counts-for-category-ranking"
          categories={categories}
          target={target}
          onTargetChange={(next) => {
            setTarget(next);
            if (next === CATEGORY_RANKING_TARGET.FIXED_CATEGORY) {
              setCategory(category ?? categories[0] ?? null);
            } else {
              setCategory(null);
            }
          }}
          categoryId={category?.id ?? null}
          onCategoryChange={(next) =>
            setCategory(categories.find((c) => c.id === next) ?? null)
          }
        />
        <p className="mt-3 text-sm text-gray-500">
          Solo se puede cambiar mientras el torneo no haya finalizado.
        </p>
      </div>
    </EditModal>
  );
}
